import React, { useEffect } from "react"
import { useState } from "react"
import { ChevronDown } from "lucide-react"
import axios, { all } from "axios"
import { base_url } from "../../config"

const CodesModal = ({ isOpen, onClose, codes }: { isOpen: boolean; onClose: () => void; codes: string[] }) => {
    if (!isOpen) return null;

    const handleCopy = (code: string) => {
        navigator.clipboard.writeText(code).then(() => {
            alert("Code copied to clipboard!");
        }).catch((error) => {
            console.error("Failed to copy code:", error);
        });
    };

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg w-full max-w-2xl p-6 relative">
                {/* Close Button */}
                <button
                    onClick={onClose}
                    className="absolute top-4 right-4 text-gray-500 hover:text-gray-700"
                    aria-label="Close"
                >
                    <svg
                        xmlns="http://www.w3.org/2000/svg"
                        width="24"
                        height="24"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                        strokeLinecap="round"
                        strokeLinejoin="round"
                    >
                        <line x1="18" y1="6" x2="6" y2="18"></line>
                        <line x1="6" y1="6" x2="18" y2="18"></line>
                    </svg>
                </button>

                {/* Modal Content */}
                <h2 className="text-xl font-bold mb-4">Generated Codes</h2>
                <div
                    className={`grid gap-4 ${codes.length > 15 ? "grid-cols-3" : "grid-cols-1"
                        }`}
                >
                    {codes.map((code, index) => (
                        <div
                            key={index}
                            className="bg-gray-100 p-2 rounded-md text-gray-800 flex justify-between items-center"
                        >
                            <span>{code}</span>
                            <button
                                onClick={() => handleCopy(code)}
                                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
                            >
                                Copy
                            </button>
                        </div>
                    ))}
                </div>
            </div>
        </div>
    );
};

export default function GiftCodeCreation() {


    const [counts, setCounts] = useState({
        vip: 3,
        volunteer: 1,
        exhibitor: 1,
        industryDay: 1,
        workshop: "",
        tutorial: "",
        all_workshops: 1,
        all_tutorials: 1,
    })

    const [modalOpen, setModalOpen] = useState(false); // Modal visibility
    const [generatedCodes, setGeneratedCodes] = useState<string[]>([]); // Store the codes
    const [workshops, setWorkshops] = useState([]); // Store the workshops
    const [tutorials, setTutorials] = useState([]); // Store the tutorials
    const handleCountChange = (type: keyof typeof counts, value: number | string) => {
        setCounts((prev) => ({ ...prev, [type]: value }))
    }

    const handleCreate = async (type: keyof typeof counts) => {

        try {

            const response = await axios.post(base_url + "/api/admin/create-codes",
                { type, count: counts[type] })
            // Implementation for code creation would go here
            setGeneratedCodes(response.data.codes); // Store the codes in state
            setModalOpen(true);
        } catch (error) {
            console.error("Error creating codes:", error);
        }
    }

    useEffect(() => {
        try {
            const fetchData = async () => {
                const response = await axios.get(base_url + "/api/admin/ws-tut");
                setWorkshops(response.data.workshops);
                setTutorials(response.data.tutorials);
            };
            fetchData();
        } catch (error) {
            console.error("Error fetching data:", error);
        }
    }, []);


    return (
        <>


            <div className="bg-gray-50 min-h-screen">


                <div className="space-y-6">
                    {/* VIP Section */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 border-l-4 border-l-blue-400">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-6 bg-gray-100 flex items-center">
                                <div className="w-2 h-2 rounded-full bg-blue-500 mr-3"></div>
                                <h2 className="text-xl font-bold text-gray-800">VIP</h2>
                            </div>

                            <div className="p-6">
                                <ul className="space-y-2 text-gray-700">
                                    <li className="font-medium">Conference</li>
                                    <li>Workshops and Tutorials</li>
                                    <li>One opening entrance</li>
                                    <li>One Gala Dinner Seat</li>
                                </ul>
                            </div>

                            <div className="p-6 flex flex-col justify-center space-y-4">
                                <div className="flex items-center space-x-3">
                                    <span className="text-sm text-gray-500">Create</span>
                                    <div className="relative">
                                        <select
                                            value={counts.vip}
                                            onChange={(e) => handleCountChange("vip", Number.parseInt(e.target.value))}
                                            className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-200 focus:border-transparent"
                                        >
                                            {[1, 2, 3, 4, 5, 10].map((num) => (
                                                <option key={num} value={num}>
                                                    {num}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    </div>
                                    <span className="text-gray-700 font-medium">VIP Codes</span>
                                </div>
                                <button
                                    onClick={() => handleCreate("vip")}
                                    className="bg-gray-700 hover:bg-gray-800 text-white font-medium py-2 px-6 rounded-md transition-colors"
                                >
                                    CREATE
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Volunteer Section */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 border-l-4 border-l-green-400">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-6 bg-gray-100 flex items-center">
                                <div className="w-2 h-2 rounded-full bg-green-500 mr-3"></div>
                                <h2 className="text-xl font-bold text-gray-800">Volunteer</h2>
                            </div>

                            <div className="p-6">
                                <ul className="space-y-2 text-gray-700">
                                    <li className="font-medium">Conference</li>
                                    <li>Workshops and Tutorials</li>
                                    <li>Opening entrance</li>
                                    <li>Student Reception</li>
                                </ul>
                            </div>

                            <div className="p-6 flex flex-col justify-center space-y-4">
                                <div className="flex items-center space-x-3">
                                    <span className="text-sm text-gray-500">Create</span>
                                    <div className="relative">
                                        <select
                                            value={counts.volunteer}
                                            onChange={(e) => handleCountChange("volunteer", Number.parseInt(e.target.value))}
                                            className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-green-200 focus:border-transparent"
                                        >
                                            {[1, 2, 3, 4, 5, 10].map((num) => (
                                                <option key={num} value={num}>
                                                    {num}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    </div>
                                    <span className="text-gray-700 font-medium">Volunteer Codes</span>
                                </div>
                                <button
                                    onClick={() => handleCreate("volunteer")}
                                    className="bg-gray-700 hover:bg-gray-800 text-white font-medium py-2 px-6 rounded-md transition-colors"
                                >
                                    CREATE
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Workshop Codes Section */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 border-l-4 border-l-purple-400">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-6 bg-gray-100 flex items-center">
                                <div className="w-2 h-2 rounded-full bg-purple-500 mr-3"></div>
                                <h2 className="text-xl font-bold text-gray-800">Workshop Codes</h2>
                            </div>

                            <div className="p-6">
                                <p className="text-gray-700">Codes that give access to a particular workshop</p>
                            </div>

                            <div className="p-6 flex flex-col justify-center space-y-4">
                                <div className="flex items-center space-x-3 w-full md:w-auto">
                                    <div className="relative flex-grow md:flex-grow-0">
                                        <select
                                            className="w-full md:w-auto appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-purple-200 focus:border-transparent"
                                            defaultValue=""
                                            onChange={(e) => handleCountChange("workshop", e.target.value)}
                                        >
                                            <option value="" disabled>
                                                Workshop
                                            </option>
                                            {workshops.map((workshop: any) => (
                                                <option key={workshop} value={workshop}>
                                                    Workshop {workshop}
                                                </option>
                                            ))}

                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    </div>
                                </div>


                                <div className="flex flex-col w-full gap-4">
                                    <button
                                        onClick={() => handleCreate("workshop")}
                                        className="flex-1 md:flex-none bg-gray-700 hover:bg-gray-800 text-white font-medium py-2 px-4 rounded-md transition-colors"

                                    >
                                        CREATE ONE CODE
                                    </button>



                                    <button
                                        onClick={() => handleCreate("all_workshops")}
                                        className="flex-1 md:flex-none bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-md transition-colors"
                                    >
                                        CREATE ALL CODES
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Tutorial Codes Section */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 border-l-4 border-l-amber-400">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-6 bg-gray-100 flex items-center">
                                <div className="w-2 h-2 rounded-full bg-amber-500 mr-3"></div>
                                <h2 className="text-xl font-bold text-gray-800">Tutorial Codes</h2>
                            </div>

                            <div className="p-6">
                                <p className="text-gray-700">Codes that give access to a particular tutorial</p>
                            </div>

                            <div className="p-6 flex flex-col justify-center space-y-4">
                                <div className="flex items-center space-x-3 w-full md:w-auto">
                                    <div className="relative flex-grow md:flex-grow-0">
                                        <select
                                            className="w-full md:w-auto appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-amber-200 focus:border-transparent"
                                            defaultValue=""
                                            onChange={(e) => handleCountChange("tutorial", e.target.value)}
                                        >
                                            <option value="" disabled>
                                                Tutorials
                                            </option>
                                            {tutorials.map((tutorial: any) => (
                                                <option key={tutorial} value={tutorial}>
                                                    Tutorial {tutorial}
                                                </option>
                                            ))}

                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    </div>
                                </div>



                                <div className="flex flex-col w-full gap-4">
                                    <button
                                        onClick={() => handleCreate("tutorial")}
                                        className="flex-1 md:flex-none bg-gray-700 hover:bg-gray-800 text-white font-medium py-2 px-4 rounded-md transition-colors"
                                    >
                                        CREATE ONE CODE
                                    </button>



                                    <button
                                        onClick={() => handleCreate("all_tutorials")}
                                        className="flex-1 md:flex-none bg-gray-200 hover:bg-gray-300 text-gray-800 font-medium py-2 px-4 rounded-md transition-colors"
                                    >
                                        CREATE ALL CODES
                                    </button>
                                </div>

                            </div>
                        </div>
                    </div>

                    {/* Exhibitor Pass Section */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 border-l-4 border-l-rose-400">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-6 bg-gray-100 flex items-center">
                                <div className="w-2 h-2 rounded-full bg-rose-500 mr-3"></div>
                                <h2 className="text-xl font-bold text-gray-800">Exhibitor Pass</h2>
                            </div>

                            <div className="p-6">
                                <ul className="space-y-2 text-gray-700">
                                    <li>Exhibitor area (4 days)</li>
                                    <li>Technical Program</li>
                                </ul>
                            </div>

                            <div className="p-6 flex flex-col justify-center space-y-4">
                                <div className="flex items-center space-x-3">
                                    <span className="text-sm text-gray-500">Create</span>
                                    <div className="relative">
                                        <select
                                            value={counts.exhibitor}
                                            onChange={(e) => handleCountChange("exhibitor", Number.parseInt(e.target.value))}
                                            className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-rose-200 focus:border-transparent"
                                        >
                                            {[1, 2, 3, 4, 5, 10].map((num) => (
                                                <option key={num} value={num}>
                                                    {num}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    </div>
                                    <span className="text-gray-700 font-medium">Exhibitor Pass Codes</span>
                                </div>
                                <button
                                    onClick={() => handleCreate("exhibitor")}
                                    className="bg-gray-700 hover:bg-gray-800 text-white font-medium py-2 px-6 rounded-md transition-colors"
                                >
                                    CREATE
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Industry Day Pass Section */}
                    <div className="bg-white rounded-lg shadow-md overflow-hidden border border-gray-200 border-l-4 border-l-cyan-400">
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="p-6 bg-gray-100 flex items-center">
                                <div className="w-2 h-2 rounded-full bg-cyan-500 mr-3"></div>
                                <h2 className="text-xl font-bold text-gray-800">Industry Day Pass</h2>
                            </div>

                            <div className="p-6">
                                <ul className="space-y-2 text-gray-700">
                                    <li>Industry Day</li>
                                </ul>
                            </div>

                            <div className="p-6 flex flex-col justify-center space-y-4">
                                <div className="flex items-center space-x-3">
                                    <span className="text-sm text-gray-500">Create</span>
                                    <div className="relative">
                                        <select
                                            value={counts.industryDay}
                                            onChange={(e) => handleCountChange("industryDay", Number.parseInt(e.target.value))}
                                            className="appearance-none bg-white border border-gray-300 rounded-md pl-3 pr-10 py-2 text-gray-700 focus:outline-none focus:ring-2 focus:ring-cyan-200 focus:border-transparent"
                                        >
                                            {[1, 2, 3, 4, 5, 10].map((num) => (
                                                <option key={num} value={num}>
                                                    {num}
                                                </option>
                                            ))}
                                        </select>
                                        <ChevronDown className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-500" />
                                    </div>
                                    <span className="text-gray-700 font-medium">Industry Day Pass Codes</span>
                                </div>
                                <button
                                    onClick={() => handleCreate("industryDay")}
                                    className="bg-gray-700 hover:bg-gray-800 text-white font-medium py-2 px-6 rounded-md transition-colors"
                                >
                                    CREATE
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            {/* Modal */}
            <CodesModal
                isOpen={modalOpen}
                onClose={() => setModalOpen(false)}
                codes={generatedCodes}
            />
        </>

    )
}